// PracticeTest.js
import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { sendApiRequest } from '../utils/api';
import {
    FaPlay, FaPause, FaCheck, FaHeadphones, FaExclamationTriangle,
    FaClock, FaBook, FaArrowRight, FaEdit, FaFileAlt, FaCog,
    FaVolumeUp, FaVolumeMute, FaChevronRight, FaListUl, FaTimes,
    FaInfoCircle, FaUserEdit, FaRegClock, FaTachometerAlt,
    FaLock, FaUnlock
} from 'react-icons/fa';
import { GiSoundWaves } from 'react-icons/gi';
import { MdSpeed, MdTimer, MdOutlineErrorOutline } from 'react-icons/md';
import TimeSetupModal from './TimeSetupModal';
import MarkingRulesModal from './MarkingRulesModal';
import Logo from "../assets/logo2.jpeg";
import LogoLoader from './LogoLoader';

function formatTime(time) {
    if (!time || isNaN(time)) return '00:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
}

const PracticeTest = () => {
    const { id } = useParams();
    const [topic, setTopic] = useState(null);
    const [loading, setLoading] = useState(true);
    const [backspaceEnabled, setBackspaceEnabled] = useState(false);
    const [error, setError] = useState('');
    const [readingTime, setReadingTime] = useState('60');
    const [checked, setChecked] = useState(false);
    const [selectedSpeed, setSelectedSpeed] = useState('');
    const [examStarted, setExamStarted] = useState(false);
    const [customTime, setCustomTime] = useState('');
    const [showTimeModal, setShowTimeModal] = useState(false);
    const audioRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const [duration, setDuration] = useState(0);
    const [countdown, setCountdown] = useState(null);
    const [showCountdown, setShowCountdown] = useState(false);
    const countdownRef = useRef(null);
    const [rules, setRules] = useState([]);
    const [selectedRule, setSelectedRule] = useState('');
    const [showCustomSection, setShowCustomSection] = useState(false);
    const [customMarkingRules, setCustomMarkingRules] = useState([]);
    const [showTranscription, setShowTranscription] = useState(false);
    const [showRulesModal, setShowRulesModal] = useState(false);
    const [volume, setVolume] = useState(0.7);
    const navigate = useNavigate();

    const commonTypingRules = [
        { id: 'spelling', name: 'Spelling Mistakes', description: 'Incorrect spelling of words', icon: '✍️' },
        { id: 'punctuation', name: 'Punctuation Errors', description: 'Missing or incorrect punctuation', icon: '🔠' },
        { id: 'capitalization', name: 'Capitalization Errors', description: 'Incorrect use of capital letters', icon: '⬆️' },
        { id: 'omission', name: 'Omission Errors', description: 'Missing words or phrases', icon: '🗑️' },
        { id: 'addition', name: 'Addition of Words Errors', description: 'Adding words not in the original text', icon: '➕' },
    ];

    useEffect(() => {
        if (topic) {
            setBackspaceEnabled(!topic.backspaceBlock);
        }
    }, [topic]);

    useEffect(() => {
        fetchRules();
        initializeCustomRules();
    }, []);

    const fetchRules = async () => {
        try {
            setLoading(true);
            const response = await sendApiRequest('api/marking-rules', {}, 'GET');
            if (response && response.length > 0) {
                setRules(response);
                setSelectedRule(response[0]._id);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const initializeCustomRules = () => {
        const initialRules = commonTypingRules.map(rule => ({
            mistakeId: rule.id,
            mistakeName: rule.name,
            mistakeDescription: rule.description,
            mistakeType: 'full',
            penaltyValue: 1
        }));
        setCustomMarkingRules(initialRules);
    };

    const handleRuleChange = (e) => {
        const value = e.target.value;
        setSelectedRule(value);
        setShowCustomSection(value === 'custom');
    };

    const handleCustomMarkingChange = (mistakeId, type) => {
        setCustomMarkingRules(prev =>
            prev.map(rule =>
                rule.mistakeId === mistakeId
                    ? {
                        ...rule,
                        mistakeType: type,
                        penaltyValue: type === 'full' ? 1 : 0.5
                    }
                    : rule
            )
        );
    };

    const toggleCustomSection = () => {
        if (showCustomSection) {
            setShowCustomSection(false);
            if (selectedRule === 'custom' && rules.length > 0) {
                setSelectedRule(rules[0]._id);
            }
        } else {
            setShowCustomSection(true);
            setSelectedRule('custom');
        }
    };

    useEffect(() => {
        return () => {
            if (countdownRef.current) {
                clearInterval(countdownRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (countdown === 0) {
            clearInterval(countdownRef.current);
            setShowCountdown(false);
            handleStartButtonClick();
        }
    }, [countdown]);

    const startCountdown = () => {
        setCountdown(5 * 60);
        setShowCountdown(true);

        if (countdownRef.current) {
            clearInterval(countdownRef.current);
        }

        countdownRef.current = setInterval(() => {
            setCountdown(prev => (prev > 0 ? prev - 1 : 0));
        }, 1000);
    };

    const calculateEstimatedDuration = (words, wpm) => {
        if (!words || !wpm || isNaN(words) || isNaN(wpm)) return '00:00';
        const totalSeconds = Math.round((words / wpm) * 60);
        return formatTime(totalSeconds);
    };

    useEffect(() => {
        window.scrollTo(0, 0);
        const fetchTopic = async () => {
            try {
                const response = await sendApiRequest(`api/admin/topics/${id}`, {}, 'GET');
                setTopic(response);
                setSelectedSpeed(response.wordsPerMin?.split(',')[0] || '');
                setCustomTime(response.transcriptionTime?.toString() || '1');
            } catch (err) {
                setError('Failed to load topic');
            } finally {
                setLoading(false);
            }
        };
        fetchTopic();
    }, [id]);

    useEffect(() => {
        if (audioRef.current && selectedSpeed) {
            let recordedWPM = 80;
            const selectedWPM = parseInt(selectedSpeed, 10);
            if (!isNaN(selectedWPM) && selectedWPM > 0) {
                let rate = selectedWPM / recordedWPM;
                rate = Math.max(0.5, Math.min(rate, 3));
                audioRef.current.playbackRate = rate;
            }
        }
    }, [selectedSpeed, examStarted, topic?.wordLength]);

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        const handlePlay = () => setIsPlaying(true);
        const handlePause = () => setIsPlaying(false);
        const handleTimeUpdate = () => setProgress(audio.currentTime);
        const handleLoadedMetadata = () => setDuration(audio.duration);
        const handleEnded = () => {
            setIsPlaying(false);
            startCountdown();
        };

        audio.addEventListener('play', handlePlay);
        audio.addEventListener('pause', handlePause);
        audio.addEventListener('timeupdate', handleTimeUpdate);
        audio.addEventListener('loadedmetadata', handleLoadedMetadata);
        audio.addEventListener('ended', handleEnded);

        setIsPlaying(!audio.paused);
        setProgress(audio.currentTime);
        if (audio.duration) {
            setDuration(audio.duration);
        }

        return () => {
            audio.removeEventListener('play', handlePlay);
            audio.removeEventListener('pause', handlePause);
            audio.removeEventListener('timeupdate', handleTimeUpdate);
            audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
            audio.removeEventListener('ended', handleEnded);
        };
    }, [topic?.mp3File?.url]);

    const handlePlayPause = () => {
        const audio = audioRef.current;
        if (!audio) return;

        if (audio.paused) {
            audio.play().catch(error => {
                console.error('Error playing audio:', error);
                setError('Failed to play audio. Please check your connection.');
            });
        } else {
            audio.pause();
        }
    };

    const handleSeek = (e) => {
        const audio = audioRef.current;
        if (!audio) return;
        const value = Number(e.target.value);
        audio.currentTime = value;
        setProgress(value);
    };

    const handleVolumeChange = (e) => {
        const value = e.target.value;
        setVolume(value);
        if (audioRef.current) {
            audioRef.current.volume = value;
        }
    };

    const handleStartButtonClick = () => {
        if (!checked) {
            alert('Please read and accept the instructions first');
            return;
        }
        // setShowTimeModal(true);
    };

    const handleConfirmTime = () => {
        setShowTimeModal(false);
        setShowRulesModal(true);
    };

    const handleConfirmRules = () => {
        setShowRulesModal(false);
        confirmStartExam();
    };

    const confirmStartExam = () => {
        let selectedRuleData;

        if (selectedRule === 'custom') {
            selectedRuleData = {
                _id: 'custom',
                title: 'Custom Marking Rules',
                rules: customMarkingRules,
                isCustom: true,
                fullMistakesCount: customMarkingRules.filter(r => r.mistakeType === 'full').length,
                halfMistakesCount: customMarkingRules.filter(r => r.mistakeType === 'half').length,
                totalRules: customMarkingRules.length,
                scoringSummary: {
                    fullMistakePenalty: 1,
                    halfMistakePenalty: 0.5
                }
            };
        } else {
            selectedRuleData = rules.find(rule => rule._id === selectedRule);
        }

        navigate(`/exam-writing/${id}`, {
            state: {
                customTime: parseInt(customTime) * 60,
                selectedSpeed,
                selectedRule: selectedRuleData,
                topic,
                backspaceEnabled
            }
        });
    };

    if (loading) {
        return (
            <LogoLoader />
        );
    }

    if (error || !topic) {
        return (
            <div className="min-h-screen bg-white flex items-center justify-center p-4">
                <div className="bg-white rounded-3xl shadow-xl p-8 text-center max-w-md border border-gray-100">
                    <div className="relative w-24 h-24 bg-gradient-to-br from-red-100 to-red-50 rounded-full flex items-center justify-center mx-auto mb-6 border border-red-100">
                        <FaExclamationTriangle className="text-red-500 text-4xl" />
                        <div className="absolute inset-0 animate-ping rounded-full bg-red-200 opacity-30"></div>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-800 mb-3">Oops! Something went wrong</h2>
                    <p className="text-gray-600 mb-6">{error || 'Topic not found'}</p>
                    <button
                        onClick={() => navigate('/dashboard')}
                        className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-8 py-3 rounded-xl font-medium transition-all transform hover:scale-105 shadow-md hover:shadow-lg"
                    >
                        Return to Dashboard
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#1d3a80]">
            {/* Background decorative elements */}

            <div className="max-w-7xl mx-auto relative z-10 bg-[#1d3a80] ">
                {/* Header */}
                <div className="px-2 py-3 bg-[#1d3a80] ">
                    <div className="max-w-3xl mx-auto flex flex-col items-center text-center text-white">

                        {/* Logo */}
                        <div className="w-28 h-28 rounded-full bg-white flex items-center justify-center shadow">
                            <img
                                src={Logo}
                                alt="Logo"
                                className="w-20 h-20 object-contain"
                            />
                        </div>

                        {/* Text Content */}
                        <div>
                            <h1 className="text-xl font-bold">
                                Shorthand Software Academy
                            </h1>
                            <p className="text-sm text-white/90 ">
                                shorthandsoftwareacademy@gmail.com
                            </p>
                            <p className="text-sm text-white/90">
                                +91 87872 24408
                            </p>
                        </div>

                    </div>
                </div>


                <div className="bg-[#1d3a80]/10 flex items-center justify-center ">

                    {/* MAIN CONTAINER */}
                    <div className="w-full max-w-4xl bg-[#FEFEFE] rounded-3xl shadow-2xl p-3 space-y-3">
                        <div className="flex items-center gap-3 mt-2">
                            <span className="text-sm font-medium text-blue-600 bg-blue-50 px-3 py-1 rounded-full border border-blue-100">
                                Exam: {topic.exam.name}
                            </span>
                            <FaChevronRight className="text-gray-300 text-xs" />
                            <span className="text-sm font-medium text-indigo-600 bg-indigo-50 px-3 py-1 rounded-full border border-indigo-100">
                                Topic: {topic.title}
                            </span>
                        </div>

                        <div className="p-1 rounded-2xl border bg-[#FEFEFE] shadow-sm">
                            <div className="flex flex-col md:flex-row items-center justify-between gap-3">

                                {/* ================= LEFT SIDE : VIEW TEXT ================= */}
                                <div className="flex items-center gap-4">
                                    <div className="w-8 h-8 rounded-xl bg-[#1d3a80]/10
                      flex items-center justify-center">
                                        <FaFileAlt className="text-[#1d3a80] text-xl" />
                                    </div>

                                    <button
                                        onClick={() => setShowTranscription(true)}
                                        className="flex items-center gap-2 bg-[#1d3a80] hover:bg-[#1f5c8f]
                   text-white px-2 py-2 rounded-xl font-semibold transition"
                                    >
                                        View Text
                                    </button>
                                </div>

                                {/* ================= RIGHT SIDE : BACKSPACE CONTROL ================= */}
                                <div className="flex items-center gap-4">

                                    <div
                                        className={`w-8 h-8 rounded-xl flex items-center justify-center
          ${backspaceEnabled ? "bg-[#1d3a80]/10" : "bg-[#B02456]/10"}`}
                                    >
                                        {backspaceEnabled
                                            ? <FaUnlock className="text-[#1d3a80]" />
                                            : <FaLock className="text-[#B02456]" />}
                                    </div>

                                    <div>
                                        <h3 className="font-semibold text-[#861426] text-sm">
                                            Backspace Control
                                        </h3>
                                        <p className="text-xs text-gray-500">
                                            Enable / Disable editing
                                        </p>
                                    </div>

                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            className="sr-only peer"
                                            checked={backspaceEnabled}
                                            onChange={() => setBackspaceEnabled(prev => !prev)}
                                        />
                                        <div className="w-14 h-7 bg-gray-300 peer-checked:bg-[#1d3a80]
                        rounded-full after:content-['']
                        after:absolute after:top-0.5 after:left-[2px]
                        after:bg-white after:h-6 after:w-6 after:rounded-full
                        after:transition peer-checked:after:translate-x-full" />
                                    </label>

                                </div>

                            </div>
                        </div>




                        {/* ================= AUDIO PLAYER ================= */}
                        <div className="p-2 rounded-2xl border shadow-sm space-y-2">

                            <audio ref={audioRef} src={topic.mp3File?.url} preload="metadata" />

                            {/* Progress */}
                            <div>
                                <div className="flex justify-between text-sm text-gray-500 mb-2">
                                    <span>{formatTime(progress)}</span>
                                    <span>{formatTime(duration)}</span>
                                </div>
                                <input
                                    type="range"
                                    min={0}
                                    max={duration}
                                    value={progress}
                                    step="0.01"
                                    onChange={handleSeek}
                                    className="w-full h-2 rounded-lg bg-gray-200
                     [&::-webkit-slider-thumb]:appearance-none
                     [&::-webkit-slider-thumb]:h-5
                     [&::-webkit-slider-thumb]:w-5
                     [&::-webkit-slider-thumb]:rounded-full
                     [&::-webkit-slider-thumb]:bg-[#1d3a80]"
                                />
                            </div>

                            {/* Controls */}
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <FaVolumeUp className="text-gray-500" />
                                    <input
                                        type="range"
                                        min="0"
                                        max="1"
                                        step="0.01"
                                        value={volume}
                                        onChange={handleVolumeChange}
                                        className="w-24"
                                    />
                                </div>

                                <button
                                    onClick={handlePlayPause}
                                    className={`w-12 h-12 rounded-full flex items-center justify-center
            text-white shadow-xl transition
            ${isPlaying
                                            ? "bg-[#B02456]"
                                            : "bg-[#1d3a80]"}`}
                                >
                                    {isPlaying ? <FaPause size={20} /> : <FaPlay size={20} />}
                                </button>

                                <div className="font-semibold text-[#861426]">
                                    {selectedSpeed} WPM
                                </div>
                            </div>
                        </div>

                        {/* ================= SPEED SELECT ================= */}
                        <div>
                            <label className="block mb-2 font-semibold text-[#861426]">
                                Select Speed (WPM)
                            </label>
                            <select
                                value={selectedSpeed || ""}
                                onChange={(e) => setSelectedSpeed(e.target.value)}
                                disabled={examStarted}
                                className="w-full px-4 py-3 rounded-xl border focus:ring-2
                   focus:ring-[#1d3a80]"
                            >
                                <option value="" disabled>Choose Speed</option>
                                {topic.wordsPerMin?.split(',').map(speed => (
                                    <option key={speed} value={speed}>
                                        {speed} WPM
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Time Setup Modal */}
                        <TimeSetupModal
                            isOpen={showTimeModal}
                            onClose={() => setShowTimeModal(false)}
                            onConfirm={handleConfirmTime}
                            customTime={customTime}
                            setCustomTime={setCustomTime}
                            topic={topic}
                            selectedSpeed={selectedSpeed}
                            calculateEstimatedDuration={calculateEstimatedDuration}
                            selectedRule={selectedRule}
                            rules={rules}
                            customMarkingRules={customMarkingRules}
                            backspaceEnabled={backspaceEnabled}
                        />

                        {/* Marking Rules Modal */}
                        <MarkingRulesModal
                            isOpen={showRulesModal}
                            onClose={() => setShowRulesModal(false)}
                            onConfirm={handleConfirmRules}
                            rules={rules}
                            selectedRule={selectedRule}
                            setSelectedRule={setSelectedRule}
                            showCustomSection={showCustomSection}
                            setShowCustomSection={setShowCustomSection}
                            customMarkingRules={customMarkingRules}
                            setCustomMarkingRules={setCustomMarkingRules}
                            handleRuleChange={handleRuleChange}
                            handleCustomMarkingChange={handleCustomMarkingChange}
                            toggleCustomSection={toggleCustomSection}
                            readingTime={readingTime}
                            setReadingTime={setReadingTime}
                        />
                        {/* ================= AGREEMENT ================= */}
                        <div className="p-1 rounded-2xl border bg-[#FEFEFE] shadow-sm">
                            <label className="flex items-start gap-4 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={e => setChecked(e.target.checked)}
                                    className="mt-1"
                                />
                                <div>
                                    <p className="font-medium text-[#861426]">
                                        I have read and understood all instructions
                                    </p>
                                </div>
                            </label>
                        </div>



                        {/* ================= START BUTTON ================= */}
                        <div className="text-center">
                            <button
                                disabled={!checked || showCountdown}
                                onClick={confirmStartExam}
                                className={`w-full py-5 rounded-2xl font-bold text-lg transition
          ${checked
                                        ? "bg-gradient-to-r from-[#1d3a80] to-[#B02456] text-white"
                                        : "bg-gray-200 text-gray-400 cursor-not-allowed"}`}
                            >
                                Start Practice Test
                            </button>
                        </div>

                    </div>
                </div>

            </div>



            {/* Transcription Modal */}
            {showTranscription && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-3xl shadow-2xl max-w-3xl w-full max-h-[85vh] flex flex-col border border-gray-100">
                        <div className="p-6 border-b border-gray-100 flex justify-between items-center">
                            <div className="flex items-center gap-4">
                                <div className="w-10 h-10 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg flex items-center justify-center border border-blue-200">
                                    <FaFileAlt className="text-blue-600" />
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold text-gray-800">Transcription Preview</h3>
                                    <p className="text-gray-500 text-sm">Read the text before starting</p>
                                </div>
                            </div>
                            <button
                                onClick={() => setShowTranscription(false)}
                                className="w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
                            >
                                <FaTimes className="text-gray-500 hover:text-gray-700" />
                            </button>
                        </div>
                        <div className="p-6 overflow-y-auto flex-1">
                            <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
                                {topic.description ? (
                                    <div className="prose max-w-none text-gray-700">
                                        <div dangerouslySetInnerHTML={{ __html: topic.description }} />
                                    </div>
                                ) : (
                                    <div className="text-center py-8">
                                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4 border border-gray-200">
                                            <FaExclamationTriangle className="text-gray-400 text-2xl" />
                                        </div>
                                        <p className="text-gray-500">No transcription available for this topic.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="p-6 border-t border-gray-100 flex justify-between">
                            <button
                                onClick={() => setShowTranscription(false)}
                                className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition-colors"
                            >
                                Close Preview
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PracticeTest;